import type {
  GetMessagesQuery,
  GetMessagesQueryVariables,
} from '#src/graphql/generated';
import type { EditorJsData } from '#src/pages/document/editorjs-types';

import { useApolloClient } from '@apollo/client/react';
import { useEffect, useRef } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { ProcessorInstanceStatus } from '#src/graphql/generated';
import { applyAskAiReactStoredProcessorResult } from '#src/pages/document/apply-document-ai-processor-result';
import { persistProcessorFailureAsDocumentRootMessage } from '#src/utils/persist-processor-failure-message';

type DocumentThreadRootMessage = GetMessagesQuery['messages'][number];
type DocumentThreadChildMessage =
  DocumentThreadRootMessage['childMessages'][number];
type DocumentThreadMessage =
  | DocumentThreadRootMessage
  | DocumentThreadChildMessage;

function* eachThreadMessage(
  roots: DocumentThreadRootMessage[],
): Generator<DocumentThreadMessage> {
  for (const root of roots) {
    yield root;
    for (const child of root.childMessages ?? []) {
      yield child;
    }
  }
}

function processorInstanceFromMessage(
  message: DocumentThreadMessage,
):
  | NonNullable<
      DocumentThreadMessage['messageContexts'][number]['processorInstance']
    >
  | undefined {
  if (!message.messageContexts) {
    return undefined;
  }
  for (const ctx of message.messageContexts) {
    if (ctx.processorInstance) {
      return ctx.processorInstance;
    }
  }
  return undefined;
}

export interface UseDocumentAiEditCompletionOptions {
  documentId: string | undefined;
  /** False after the first `GET_MESSAGES` result for this panel (even if empty). */
  isMessagesInitialLoad: boolean;
  sortedMessages: GetMessagesQuery['messages'];
  onValueUpdate: (value: EditorJsData | null) => void;
  getMessagesVariables: GetMessagesQueryVariables;
}

/**
 * When document messages refetch (e.g. after `inboxMessagesUpdated` subscription), applies
 * completed ask-ai-react processor results (including runs started via the `ask-ai` reaction).
 * Historical `Done` instances from the initial load are recorded but not applied to the editor.
 */
export function useDocumentAiEditCompletion({
  documentId,
  isMessagesInitialLoad,
  sortedMessages,
  onValueUpdate,
  getMessagesVariables,
}: UseDocumentAiEditCompletionOptions): void {
  const apolloClient = useApolloClient();
  const appliedProcessorInstanceIdsRef = useRef<Set<string>>(new Set());
  const baselineSeededRef = useRef(false);

  useEffect(() => {
    baselineSeededRef.current = false;
    appliedProcessorInstanceIdsRef.current = new Set();
  }, [documentId]);

  useEffect(() => {
    if (!documentId || isMessagesInitialLoad) {
      return;
    }

    if (!baselineSeededRef.current) {
      for (const msg of eachThreadMessage(sortedMessages)) {
        const pi = processorInstanceFromMessage(msg);
        if (pi?.status === ProcessorInstanceStatus.Done) {
          appliedProcessorInstanceIdsRef.current.add(pi.id);
        }
      }
      baselineSeededRef.current = true;
      return;
    }

    void (async () => {
      const persistDocError = async (
        message: string,
        instanceId?: string | null,
      ) => {
        const persist = await persistProcessorFailureAsDocumentRootMessage(
          apolloClient,
          {
            documentId,
            errorMessage: message,
            processorInstanceId: instanceId,
            getMessagesVariables,
          },
        );
        if (!persist.ok) {
          toaster.create({
            title: 'Could not save error to messages',
            description: persist.error,
            type: 'warning',
          });
        }
      };

      for (const msg of eachThreadMessage(sortedMessages)) {
        const pi = processorInstanceFromMessage(msg);
        if (!pi) {
          continue;
        }
        if (appliedProcessorInstanceIdsRef.current.has(pi.id)) {
          continue;
        }
        if (
          pi.status === ProcessorInstanceStatus.Queued ||
          pi.status === ProcessorInstanceStatus.Running
        ) {
          continue;
        }

        if (pi.status === ProcessorInstanceStatus.Done) {
          appliedProcessorInstanceIdsRef.current.add(pi.id);
          if (pi.error != null && String(pi.error).length > 0) {
            await persistDocError(String(pi.error), pi.id);
          } else {
            await applyAskAiReactStoredProcessorResult(pi.value, {
              onValueUpdate,
              persistDocError,
              instanceId: pi.id,
            });
          }
        }
      }
    })();
  }, [
    apolloClient,
    documentId,
    getMessagesVariables,
    isMessagesInitialLoad,
    onValueUpdate,
    sortedMessages,
  ]);
}
